/**
 * React Query hooks for issue evidence, gates, and telemetry.
 * Wraps existing evidenceApi and telemetryApi with proper caching/polling.
 */

import { useQuery } from '@tanstack/react-query';
import { evidenceApi, type LifecycleResponse, type GatesResponse } from '../api/evidence';
import { telemetryApi, type TelemetryEventsResponse } from '../api/telemetry';

export function useIssueLifecycle(issueId: string | undefined) {
  return useQuery<LifecycleResponse>({
    queryKey: ['evidence', 'lifecycle', issueId],
    queryFn: () => evidenceApi.getLifecycle(issueId!),
    enabled: !!issueId,
    refetchInterval: 10_000,
    staleTime: 5_000,
    retry: false,
  });
}

export function useIssueGates(issueId: string | undefined) {
  return useQuery<GatesResponse>({
    queryKey: ['evidence', 'gates', issueId],
    queryFn: () => evidenceApi.getGates(issueId!),
    enabled: !!issueId,
    staleTime: 10_000,
    retry: false,
  });
}

export function useIssueTelemetry(issueId: string | undefined) {
  return useQuery<TelemetryEventsResponse>({
    queryKey: ['telemetry', 'events', { issueId }],
    queryFn: () => telemetryApi.getEvents({ issueId: issueId!, limit: 50 }),
    enabled: !!issueId,
    refetchInterval: 10_000,
    staleTime: 5_000,
    retry: false,
  });
}

/**
 * Fetch telemetry events by workflowId (the sprint's batchId).
 * Used as a fallback when issue-level telemetry is empty.
 */
export function useSprintTelemetry(workflowId: string | undefined) {
  return useQuery<TelemetryEventsResponse>({
    queryKey: ['telemetry', 'events', { workflowId }],
    queryFn: () => telemetryApi.getEvents({ workflowId: workflowId!, limit: 100 }),
    enabled: !!workflowId,
    refetchInterval: 10_000,
    staleTime: 5_000,
    retry: false,
  });
}
